import { IUser } from '@aca-new/app/pages/user/shared/models/interfaces/user.interface';
import { DialogRef } from '@aca-new/app/shared/components/modal/shared/classes/dialog.ref';
import { DIALOG_DATA } from '@aca-new/app/shared/components/modal/shared/tokens/dialog.token';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { IQualityManualData } from '@aca-new/app/shared/models/interfaces/quality-manual.interface';
import { QimaNullableType } from '@aca-new/app/shared/models/types/qima.type';
import { QimaButtonService } from '@aca-new/app/shared/services/exported-services/qima-button/qima-button.service';
import { QimaIconService } from '@aca-new/app/shared/services/exported-services/qima-icon/qima-icon.service';
import { QimaTooltipService } from '@aca-new/app/shared/services/exported-services/qima-tooltip/qima-tooltip.service';
import { AppFileDownloadService } from '@aca-new/app/shared/services/file-services/app-file-download.service';
import { AppSnackbarService } from '@aca-new/app/shared/services/modal-services/app-snackbar/app-snackbar.service';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import {
  EQimaButtonSize,
  EQimaButtonWidth,
  EQimaCheckboxState,
  EQimaSnackbarType,
  QimaButtonIconModule,
  QimaButtonModule,
  QimaInputCheckboxModule,
  QimaTooltipDirectiveModule,
} from '@qima/ngx-qima';
import moment from 'moment';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

interface IDialogData {
  onAcknowledge: (companyId: string) => Observable<void>;
  qualityManualData: IQualityManualData;
  userData: IUser;
}

@UntilDestroy()
@Component({
  selector: 'app-acknowledge-quality-manual',
  templateUrl: './acknowledge-quality-manual.component.html',
  styleUrls: ['./acknowledge-quality-manual.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, FormsModule, QimaButtonIconModule, QimaButtonModule, QimaInputCheckboxModule, QimaTooltipDirectiveModule, TranslateModule],
})
export class AcknowledgeQualityManualComponent implements OnInit {
  public readonly buttonWidthFull = EQimaButtonWidth.FULL;
  public readonly buttonSizeLarge = EQimaButtonSize.LARGE;
  public readonly checkboxStateEnum = EQimaCheckboxState;

  public masterAccount: string = '';
  public publishDate: string = '';
  public filename: string = '';
  public modalContentKey: string = 'ACKNOWLEDGE_QUALITY_MANUAL.MODAL_CONTENT';
  public isAcknowledged: EQimaCheckboxState = EQimaCheckboxState.UNCHECKED;
  public isButtonDisabled: boolean = true;

  private _companyId: string = '';
  private _parentCompanyId: string = '';

  public constructor(
    public readonly qimaButton: QimaButtonService,
    @Inject(DIALOG_DATA) private readonly _dialogData: QimaNullableType<IDialogData>,
    private readonly _dialogRef: DialogRef<void>,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _appFileDownloadService: AppFileDownloadService,
    private readonly _appSnackbarService: AppSnackbarService,
    public readonly qimaIcon: QimaIconService,
    public readonly qimaTooltip: QimaTooltipService
  ) {}

  public ngOnInit(): void {
    if (!this._dialogData) {
      return;
    }

    const { qualityManualData, userData } = this._dialogData;

    this.masterAccount = userData.company.parentCompanyName;
    this._companyId = userData.company.id;
    this._parentCompanyId = userData.company.parentCompanyId;
    this.filename = qualityManualData.filename;

    const publishDateMoment = moment(qualityManualData.publishDate);

    this.publishDate = publishDateMoment.isValid() ? publishDateMoment.format('DD-MMM-YYYY') : '';
    this.modalContentKey = this.publishDate ? 'ACKNOWLEDGE_QUALITY_MANUAL.MODAL_CONTENT' : 'ACKNOWLEDGE_QUALITY_MANUAL.MODAL_CONTENT_WITHOUT_DATE';

    this._changeDetectorRef.markForCheck();
  }

  public onCheckboxChange(): void {
    this.isButtonDisabled = this.isAcknowledged !== EQimaCheckboxState.CHECKED;
    this._changeDetectorRef.markForCheck();
  }

  public onDownload(): void {
    if (!this._parentCompanyId || !this.filename) {
      return;
    }

    const url = `${SERVER_URL}/company/${this._parentCompanyId}/download/quality-manual-file`;

    this._appFileDownloadService
      .downloadViaBlob$(url, this.filename, [])
      .pipe(take(1), untilDestroyed(this))
      .subscribe((isSuccess: boolean): void => {
        if (!isSuccess) {
          this._appSnackbarService.showSnackbar('ACKNOWLEDGE_QUALITY_MANUAL.DOWNLOAD_ERROR_MESSAGE', EQimaSnackbarType.ERROR);
        }
      });
  }

  public onClose(): void {
    this._dialogRef.close();
  }

  public onAcknowledge(): void {
    if (this.isButtonDisabled || !this._dialogData) {
      return;
    }

    this._dialogData
      .onAcknowledge(this._companyId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (): void => {
          this._dialogRef.close();
        },
      });
  }
}
